import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const ShimmerBlock = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${(props) => props.radius || "4px"};
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "16px"};
`;

/* Matches Layout Wrapper padding */
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
  padding: 32px 256px 55px 256px;
  @media (max-width: 1400px) {
    padding: 32px 160px 100px 160px;
  }
  @media (max-width: 1100px) {
    padding: 32px 96px 60px 96px;
  }
  @media (max-width: 778px) {
    padding: 32px 48px 40px 48px;
  }
  @media (max-width: 500px) {
    padding: 32px 32px 30px 32px;
  }
`;

const ContentColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
`;

/* Header skeleton */
const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
`;

/* Banner skeleton — matches new UI banner; same responsive heights */
const BannerSkeleton = styled.div`
  width: 100%;
  max-width: 1000px;
  height: 300px;
  margin: 0 auto;
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;

  @media screen and (max-width: 700px) {
    height: 220px;
    border-radius: 16px;
  }
  @media screen and (max-width: 500px) {
    height: 180px;
    border-radius: 12px;
  }
`;

/* Category grid — matches CategoryHome grid */
const CategoryGrid = styled.div`
  display: grid;
  gap: 24px;
  padding-top: 32px;
  grid-template-columns: repeat(3, 1fr);

  @media (max-width: 1258px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

/* Card skeleton — matches CategoryWidget */
const CardSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;
  min-height: 200px;
  width: 100%;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
`;

const CardIcon = styled(ShimmerBlock)``;

const CardLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin-top: 12px;
`;

const CardMeta = styled.div`
  display: flex;
  gap: 16px;
  margin-top: auto;
  padding-top: 24px;
`;

function SkeletonCard() {
  return (
    <CardSkeleton>
      <CardIcon width="36px" height="36px" radius="8px" />
      <CardLines>
        <ShimmerBlock width="70%" height="18px" radius="4px" />
        <ShimmerBlock width="90%" height="14px" radius="4px" />
        <ShimmerBlock width="60%" height="14px" radius="4px" />
      </CardLines>
      <CardMeta>
        <ShimmerBlock width="80px" height="12px" radius="4px" />
        <ShimmerBlock width="80px" height="12px" radius="4px" />
      </CardMeta>
    </CardSkeleton>
  );
}

export const NewCategoryLoader = ({ cardCount = 6 }) => {
  return (
    <Wrapper>
      <ContentColumn>
        {/* Header skeleton */}
        <HeaderRow>
          <ShimmerBlock width="120px" height="40px" radius="6px" />
          <ShimmerBlock width="100px" height="36px" radius="6px" />
        </HeaderRow>

        {/* Banner skeleton */}
        <BannerSkeleton>
          <ShimmerBlock width="360px" height="28px" radius="6px" />
          <ShimmerBlock width="480px" height="48px" radius="10px" />
        </BannerSkeleton>

        {/* Category cards grid */}
        <CategoryGrid>
          {Array.from({ length: cardCount }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </CategoryGrid>
      </ContentColumn>
    </Wrapper>
  );
};
